import { Checkbox, Skeleton, TableCell, TableRow } from '@mui/material'
import PropTypes from 'prop-types'

/**
 * TbxSheetsTableSkeleton - Skeleton loading rows for material sheets table
 *
 * Displays 5 skeleton table rows matching the sheets table structure.
 * Designed to be used within an existing TableBody element.
 *
 * Column structure:
 * - Base columns: checkbox, thickness, size, sheet rate, extra markup, applied markup, expiry, last used, actions
 * - Rotary material adds: profile (after thickness), length (after size)
 * - showPricing adds: sheet cost (after length/size), consumption mode (after last used)
 * - Dynamic cutting technology columns (after consumption mode/last used)
 *
 * @param {Object} props
 * @param {boolean} [props.isRotary=false] - Whether the material is Rotary type
 * @param {boolean} [props.showPricing=true] - Whether to show pricing columns
 * @param {number} [props.cuttingTechnologiesCount=0] - Number of cutting technology columns
 * @returns {JSX.Element}
 */
const TbxSheetsTableSkeleton = ({ cuttingTechnologiesCount = 0, isRotary = false, showPricing = true }) => {
    // Generate 5 fixed skeleton rows
    const rows = Array.from({ length: 10 }, (_, index) => index)

    return (
        <>
            {rows.map((rowIndex) => (
                <TableRow key={`skeleton-row-${rowIndex}`}>
                    {/* Checkbox column */}
                    <TableCell
                        padding="checkbox"
                        style={{ width: '48px' }}
                    >
                        <Checkbox disabled />
                    </TableCell>

                    {/* Thickness column - right aligned */}
                    <TableCell
                        align="right"
                        style={{ width: '80px' }}
                        sx={{ padding: '5px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={50}
                        />
                    </TableCell>

                    {/* Profile column - conditional for Rotary */}
                    {isRotary ? (
                        <TableCell
                            align="left"
                            style={{ width: '100px' }}
                            sx={{ padding: '5px' }}
                        >
                            <Skeleton
                                height={20}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={70}
                            />
                        </TableCell>
                    ) : null}

                    {/* Size column - right aligned */}
                    <TableCell
                        align="right"
                        style={{ width: '100px' }}
                        sx={{ padding: '5px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={80}
                        />
                    </TableCell>

                    {/* Length column - conditional for Rotary, right aligned */}
                    {isRotary ? (
                        <TableCell
                            align="right"
                            style={{ width: '80px' }}
                            sx={{ padding: '5px' }}
                        >
                            <Skeleton
                                height={20}
                                sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                                variant="rectangular"
                                width={50}
                            />
                        </TableCell>
                    ) : null}

                    {/* Sheet cost column - conditional on showPricing, right aligned */}
                    {showPricing ? (
                        <TableCell
                            align="right"
                            style={{ width: '80px' }}
                            sx={{ padding: '5px' }}
                        >
                            <Skeleton
                                height={20}
                                sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                                variant="rectangular"
                                width={60}
                            />
                        </TableCell>
                    ) : null}

                    {/* Sheet rate column - right aligned */}
                    <TableCell
                        align="right"
                        style={{ width: '80px' }}
                        sx={{ padding: '5px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={60}
                        />
                    </TableCell>

                    {/* Extra markup column - right aligned */}
                    <TableCell
                        align="right"
                        style={{ width: '80px' }}
                        sx={{ padding: '5px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={40}
                        />
                    </TableCell>

                    {/* Applied markup column - right aligned */}
                    <TableCell
                        align="right"
                        style={{ width: '80px' }}
                        sx={{ padding: '5px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={40}
                        />
                    </TableCell>

                    {/* Expiry column - right aligned */}
                    <TableCell
                        align="right"
                        style={{ width: '100px' }}
                        sx={{ padding: '5px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={80}
                        />
                    </TableCell>

                    {/* Last used column - right aligned */}
                    <TableCell
                        align="right"
                        style={{ width: '100px' }}
                        sx={{ padding: '5px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={80}
                        />
                    </TableCell>

                    {/* Consumption mode column - conditional on showPricing */}
                    {showPricing ? (
                        <TableCell
                            style={{ width: '100px' }}
                            sx={{ padding: '5px' }}
                        >
                            <Skeleton
                                height={20}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={90}
                            />
                        </TableCell>
                    ) : null}

                    {/* Cutting technologies columns - dynamic based on count */}
                    {Array.from({ length: cuttingTechnologiesCount }, (_, techIndex) => (
                        <TableCell
                            key={`tech-${techIndex}`}
                            style={{ width: '40px' }}
                            sx={{ padding: '5px' }}
                        >
                            <Skeleton
                                height={20}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={30}
                            />
                        </TableCell>
                    ))}

                    {/* Actions column - right aligned */}
                    <TableCell
                        align="right"
                        style={{ width: '100px' }}
                        sx={{ padding: '5px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={70}
                        />
                    </TableCell>
                </TableRow>
            ))}
        </>
    )
}

TbxSheetsTableSkeleton.propTypes = {
    cuttingTechnologiesCount: PropTypes.number,
    isRotary: PropTypes.bool,
    showPricing: PropTypes.bool,
}

export default TbxSheetsTableSkeleton
